import { useEffect, useRef, useState } from 'react';
import { sankey, sankeyLinkHorizontal, SankeyNode, SankeyLink } from 'd3-sankey';

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

// Map AWS service names → category
const SERVICE_CATEGORY: Record<string, string> = {
  'Amazon EC2': 'Compute',
  'Amazon ECS': 'Compute',
  'Amazon EKS': 'Compute',
  'AWS Lambda': 'Compute',
  'AWS Fargate': 'Compute',
  'Amazon Lightsail': 'Compute',
  'Amazon S3': 'Storage',
  'Amazon EBS': 'Storage',
  'Amazon EFS': 'Storage',
  'Amazon Glacier': 'Storage',
  'AWS Backup': 'Storage',
  'Amazon RDS': 'Database',
  'Amazon DynamoDB': 'Database',
  'Amazon ElastiCache': 'Database',
  'Amazon Redshift': 'Database',
  'Amazon Aurora': 'Database',
  'Amazon CloudFront': 'Networking',
  'Amazon Route 53': 'Networking',
  'AWS Direct Connect': 'Networking',
  'Amazon VPC': 'Networking',
  'AWS Transfer Family': 'Networking',
  'Amazon API Gateway': 'Serverless',
  'AWS Step Functions': 'Serverless',
  'Amazon SQS': 'Serverless',
  'Amazon SNS': 'Serverless',
  'Amazon EventBridge': 'Serverless',
  'Amazon SageMaker': 'ML & Analytics',
  'Amazon Bedrock': 'ML & Analytics',
  'Amazon Athena': 'ML & Analytics',
  'Amazon Kinesis': 'ML & Analytics',
  'Amazon EMR': 'ML & Analytics',
  'Amazon QuickSight': 'ML & Analytics',
  'AWS Glue': 'ML & Analytics',
  'AWS CloudTrail': 'Management',
  'Amazon CloudWatch': 'Management',
  'AWS Config': 'Management',
  'AWS Systems Manager': 'Management',
  'AWS Support': 'Management',
  'AWS Secrets Manager': 'Management',
  'Amazon GuardDuty': 'Management',
  'Amazon WAF': 'Management',
};

const CATEGORY_COLORS: Record<string, string> = {
  'Compute':       '#FF9900',
  'Storage':       '#7AA116',
  'Database':      '#C7131F',
  'Networking':    '#8C4FFF',
  'Serverless':    '#E7157B',
  'ML & Analytics':'#01A88D',
  'Management':    '#232F3E',
  'Other':         '#6B7280',
};

function getCategory(serviceName: string): string {
  for (const [key, cat] of Object.entries(SERVICE_CATEGORY)) {
    if (serviceName.includes(key) || key.includes(serviceName)) return cat;
  }
  // Fuzzy fallback
  if (serviceName.includes('EC2') || serviceName.includes('Compute') || serviceName.includes('EKS') || serviceName.includes('ECS') || serviceName.includes('Lambda') || serviceName.includes('Fargate')) return 'Compute';
  if (serviceName.includes('S3') || serviceName.includes('Storage') || serviceName.includes('EBS') || serviceName.includes('EFS')) return 'Storage';
  if (serviceName.includes('RDS') || serviceName.includes('DynamoDB') || serviceName.includes('Database') || serviceName.includes('Aurora') || serviceName.includes('ElastiCache') || serviceName.includes('Redshift')) return 'Database';
  if (serviceName.includes('CloudFront') || serviceName.includes('Route') || serviceName.includes('VPC') || serviceName.includes('Network') || serviceName.includes('Transfer')) return 'Networking';
  if (serviceName.includes('Lambda') || serviceName.includes('API Gateway') || serviceName.includes('SQS') || serviceName.includes('SNS') || serviceName.includes('EventBridge')) return 'Serverless';
  if (serviceName.includes('SageMaker') || serviceName.includes('Athena') || serviceName.includes('Kinesis') || serviceName.includes('EMR') || serviceName.includes('Glue') || serviceName.includes('Bedrock')) return 'ML & Analytics';
  if (serviceName.includes('CloudWatch') || serviceName.includes('CloudTrail') || serviceName.includes('Config') || serviceName.includes('Support') || serviceName.includes('GuardDuty') || serviceName.includes('WAF') || serviceName.includes('Secrets')) return 'Management';
  return 'Other';
}

interface Props {
  serviceBreakdown: Record<string, number>;
  totalSpend: number;
  isDark: boolean;
}

interface NodeData {
  name: string;
  type: 'root' | 'category' | 'service';
  color: string;
  value?: number;
}

type SNode = SankeyNode<NodeData, object>;
type SLink = SankeyLink<NodeData, object>;

export default function CostSankey({ serviceBreakdown, totalSpend, isDark }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string; value: string } | null>(null);
  const [dims, setDims] = useState({ width: 800, height: 500 });

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      setDims({ width: Math.max(width, 400), height: Math.max(Math.round(width * 0.55), 340) });
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Build nodes and links
  const entries = Object.entries(serviceBreakdown)
    .filter(([, v]) => v > 0.01)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 20); // cap at 20 services for readability

  // Aggregate into categories
  const categoryTotals: Record<string, number> = {};
  for (const [svc, cost] of entries) {
    const cat = getCategory(svc);
    categoryTotals[cat] = (categoryTotals[cat] || 0) + cost;
  }

  const categories = Object.keys(categoryTotals).sort((a, b) => categoryTotals[b] - categoryTotals[a]);

  // Node list: root → categories → services
  const nodeNames: NodeData[] = [
    { name: 'Total Spend', type: 'root', color: '#0F3D2E' },
    ...categories.map(cat => ({ name: cat, type: 'category' as const, color: CATEGORY_COLORS[cat] || '#6B7280' })),
    ...entries.map(([svc, cost]) => ({ name: svc, type: 'service' as const, color: CATEGORY_COLORS[getCategory(svc)] || '#6B7280', value: cost })),
  ];

  const nodeIndex = (name: string) => nodeNames.findIndex(n => n.name === name);

  const links: { source: number; target: number; value: number }[] = [
    // root → categories
    ...categories.map(cat => ({ source: 0, target: nodeIndex(cat), value: categoryTotals[cat] })),
    // categories → services
    ...entries.map(([svc, cost]) => ({
      source: nodeIndex(getCategory(svc)),
      target: nodeIndex(svc),
      value: cost,
    })),
  ];

  const { width, height } = dims;
  const pad = { top: 20, right: 160, bottom: 20, left: 20 };

  const sankeyGen = sankey<NodeData, object>()
    .nodeWidth(18)
    .nodePadding(10)
    .extent([[pad.left, pad.top], [width - pad.right, height - pad.bottom]]);

  let graph: { nodes: SNode[]; links: SLink[] } | null = null;
  try {
    graph = sankeyGen({
      nodes: nodeNames.map(d => ({ ...d })),
      links: links.map(d => ({ ...d })),
    });
  } catch {
    graph = null;
  }

  const textColor = isDark ? 'rgba(255,255,255,0.75)' : 'rgba(15,22,32,0.7)';
  const labelBg = isDark ? 'rgba(15,22,32,0.85)' : 'rgba(255,255,255,0.92)';

  if (!graph) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: textColor, fontFamily: FONT, fontSize: '14px' }}>
        Not enough data to render flow diagram
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* Links */}
        {graph.links.map((link, i) => {
          const d = sankeyLinkHorizontal()(link as any) || '';
          const sourceNode = link.source as SNode;
          const targetNode = link.target as SNode;
          const color = sourceNode.color || targetNode.color || '#888';
          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={color}
              strokeOpacity={0.2}
              strokeWidth={Math.max(1, (link.width || 1))}
              style={{ cursor: 'pointer', transition: 'stroke-opacity 0.2s' }}
              onMouseEnter={e => {
                (e.target as SVGPathElement).style.strokeOpacity = '0.5';
                const sName = sourceNode.name;
                const tName = targetNode.name;
                setTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  label: `${sName} → ${tName}`,
                  value: `$${(link.value as number).toFixed(2)}`,
                });
              }}
              onMouseLeave={e => {
                (e.target as SVGPathElement).style.strokeOpacity = '0.2';
                setTooltip(null);
              }}
            />
          );
        })}

        {/* Nodes */}
        {graph.nodes.map((node, i) => {
          const x0 = node.x0 ?? 0;
          const x1 = node.x1 ?? 0;
          const y0 = node.y0 ?? 0;
          const y1 = node.y1 ?? 0;
          const nodeH = Math.max(y1 - y0, 1);
          const isRight = node.type === 'service';
          const labelX = isRight ? x1 + 8 : x0 - 8;
          const labelAnchor = isRight ? 'start' : 'end';

          // Truncate long service names
          const rawName = node.name.replace('Amazon ', '').replace('AWS ', '');
          const label = rawName.length > 22 ? rawName.slice(0, 20) + '…' : rawName;

          return (
            <g key={i}
              style={{ cursor: 'pointer' }}
              onMouseEnter={e => {
                setTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  label: node.name,
                  value: `$${(node.value as number || 0).toFixed(2)}`,
                });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              <rect
                x={x0}
                y={y0}
                width={x1 - x0}
                height={nodeH}
                rx={4}
                fill={node.color || '#888'}
                opacity={0.9}
              />
              <text
                x={labelX}
                y={y0 + nodeH / 2}
                dy="0.35em"
                textAnchor={labelAnchor}
                fontSize={node.type === 'root' ? 13 : node.type === 'category' ? 12 : 11}
                fontWeight={node.type === 'root' ? 700 : node.type === 'category' ? 600 : 400}
                fontFamily={FONT}
                fill={textColor}
              >
                {node.type === 'root' ? 'Total' : label}
              </text>
              {/* Value label for categories */}
              {node.type === 'category' && (
                <text
                  x={labelX}
                  y={y0 + nodeH / 2 + 14}
                  textAnchor={labelAnchor}
                  fontSize={10}
                  fontFamily={FONT}
                  fill={isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,22,32,0.35)'}
                >
                  ${(node.value as number || 0).toFixed(0)}
                </text>
              )}
            </g>
          );
        })}

        {/* Total label above root node */}
        {graph.nodes[0] && (() => {
          const root = graph!.nodes[0];
          return (
            <text
              x={(root.x0 ?? 0) + ((root.x1 ?? 0) - (root.x0 ?? 0)) / 2}
              y={(root.y0 ?? 0) - 8}
              textAnchor="middle"
              fontSize={11}
              fontFamily={FONT}
              fontWeight={600}
              fill={isDark ? 'rgba(78,125,94,0.9)' : '#0F3D2E'}
            >
              ${totalSpend.toFixed(2)}
            </text>
          );
        })()}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: tooltip.x + 12,
          top: tooltip.y - 10,
          background: labelBg,
          border: '1px solid rgba(15,61,46,0.15)',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          fontFamily: FONT,
          color: isDark ? 'rgba(255,255,255,0.85)' : 'rgb(22,30,45)',
          pointerEvents: 'none',
          zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          whiteSpace: 'nowrap',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '2px' }}>{tooltip.label}</div>
          <div style={{ color: isDark ? '#E8B84C' : '#0F3D2E', fontWeight: 700 }}>{tooltip.value}</div>
        </div>
      )}
    </div>
  );
}
